<div class="loading-spinner" *ngIf="isLoading; else loaded">
  <div class="cc-icon cc-icon-spinner"></div>
</div>
<ng-template #loaded>
  <form #form="ngForm" novalidate (submit)="form.valid && onSubmit()" class="sm-flex sm-flex-col sm-gap-4">
    <h1 class="sm-text-xl sm-font-medium" i18n>Global Billing</h1>

    <p i18n>These billing settings apply to all of your product subscriptions (not only Dashpivot).</p>

    <!-- Billing Company -->
    <div class="row sm-items-center">
      <label i18n for="billingCompany" class="default-label col-form-label col-sm-4">Billing company</label>
      <div class="col-sm-8 input-group">
        <cc-text-input
          name="billingCompany"
          id="billingCompany"
          data-test="billing-company-input"
          [(value)]="formModel.billingCompany"
          [disabled]="!isSuperAdmin"
          (onValueChanged)="flagTextInputChange()"
        ></cc-text-input>
      </div>
    </div>

    <!-- Billing Address -->
    <div class="row sm-items-center">
      <label i18n for="billingAddress" class="default-label col-form-label col-sm-4">Billing address</label>
      <div class="col-sm-8 input-group">
        <cc-text-input
          name="billingAddress"
          id="billingAddress"
          data-test="billing-address-input"
          [(value)]="formModel.billingAddress"
          [disabled]="!isSuperAdmin"
          (onValueChanged)="flagTextInputChange()"
        ></cc-text-input>
      </div>
    </div>

    <!-- Billing Contact -->
    <div class="row sm-items-center">
      <label i18n for="billingContact" class="default-label col-form-label col-sm-4">Billing contact</label>
      <div class="col-sm-8 input-group">
        <cc-text-input
          name="billingContact"
          id="billingContact"
          data-test="billing-contact-input"
          [(value)]="formModel.billingContact"
          [disabled]="!isSuperAdmin"
          (onValueChanged)="flagTextInputChange()"
        ></cc-text-input>
      </div>
    </div>

    <!-- Billing Date -->
    <div class="row sm-items-center" data-test="billingDate">
      <label i18n for="billingDate" class="default-label col-form-label col-sm-4">Billing date</label>
      <div class="col-sm-8 input-group date-selector-container">
        <input
          #billingDatePicker="ngbDatepicker"
          [class.disabled]="!isSuperAdmin"
          class="input-text-base date-picker"
          [readOnly]="!isSuperAdmin"
          placeholder="dd-mm-yyyy"
          name="billingDate"
          ngbDatepicker
          container="body"
          [(ngModel)]="formModel.billingDate"
        />
        <div class="input-group-append">
          <button
            type="button"
            class="btn date-picker-btn input-text-base"
            [class.disabled]="!isSuperAdmin"
            data-test="billing-date-selector"
            (click)="canDisplayDatePicker($event) && billingDatePicker.toggle()"
          >
            <i class="cc-icon cc-icon-calendar"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Initial Subscription Date -->
    <div class="row sm-items-center" data-test="initialSubscriptionDate">
      <label i18n for="initialSubscriptionDate" class="default-label col-form-label col-sm-4"
        >Initial subscription date</label
      >
      <div class="col-sm-8 input-group date-selector-container">
        <input
          #subscriptionDatePicker="ngbDatepicker"
          [class.disabled]="!isSuperAdmin"
          class="input-text-base date-picker"
          [readOnly]="!isSuperAdmin"
          placeholder="dd-mm-yyyy"
          name="initialSubscriptionDate"
          ngbDatepicker
          container="body"
          [(ngModel)]="formModel.initialSubscriptionDate"
        />
        <div class="input-group-append">
          <button
            type="button"
            class="btn date-picker-btn input-text-base"
            [class.disabled]="!isSuperAdmin"
            data-test="initial-subscription-date-selector"
            (click)="canDisplayDatePicker($event) && subscriptionDatePicker.toggle()"
          >
            <i class="cc-icon cc-icon-calendar"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <label i18n for="paymentTerm" class="default-label col-form-label col-sm-4">Payment term</label>
      <div class="payment-term-day">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="paymentTermDaySelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.paymentTermDay"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="paymentTermDay"
          id="paymentTermDay"
        >
        </ng-select>
      </div>

      <div class="payment-term-context">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="paymentTermContextSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.paymentTermContext"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="paymentTermContext"
          id="paymentTermContext"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Payment Method -->
    <div class="row">
      <label i18n for="paymentMethod" class="default-label col-form-label col-sm-4">Payment method</label>
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="paymentMethodSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.paymentMethod"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="paymentMethod"
          id="paymentMethod"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Payment Currency -->
    <div class="row">
      <label i18n for="paymentCurrency" class="default-label col-form-label col-sm-4">Currency</label>
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="paymentCurrencySelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.paymentCurrency"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="paymentCurrency"
          id="paymentCurrency"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Account Type -->
    <div class="row">
      <label i18n for="accountType" class="default-label col-form-label col-sm-4">Account type</label>
      <div class="col-sm-8">
        <ng-select
          [readonly]="!isSuperAdmin"
          [items]="accountTypeSelectorValues"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="formModel.accountType"
          [class.disabled]="!isSuperAdmin"
          class="dropdown-selector sm-w-full"
          name="accountType"
          id="accountType"
          placeholder="Select..."
        >
        </ng-select>
      </div>
    </div>

    <!-- Automated billing -->
    <div class="row" data-test="automated-billing-input-toggle">
      <label i18n class="default-label col-form-label col-sm-4">Automated billing</label>
      <div class="col-sm-8">
        <cc-input-toggle
          class="sm-flex sm-items-center sm-gap-x-2"
          [(modelValue)]="formModel.automatedBilling"
          [isDisabled]="!isSuperAdmin"
          (onUpdate)="automatedBillingToggleClicked($event)"
        >
        </cc-input-toggle>
      </div>
    </div>

    <!-- Save -->
    <div class="sm-mb-8 sm-text-left">
      <button
        type="submit"
        data-test="save-button"
        class="sm-p-2 button-green sm-mb-6 sm-rounded button-green"
        [class.disabled]="!canSaveSettings || isSaving"
        (click)="onSubmit($event)"
      >
        Save changes
      </button>
    </div>
  </form>
</ng-template>
